import React, { useEffect, useState } from "react";
import { pdfjsLib, renderPageToDataUrl } from "./pdfUtils";

const THUMB_WIDTH = 320;
// Bump to invalidate previously cached covers when rendering changes
const CACHE_VERSION = "v2";

// In-memory cache + sessionStorage so covers aren't re-rendered on
// every visit within a session.
const memCache = new Map();

// Simple semaphore — render at most 3 covers at a time so a long list
// doesn't fire dozens of parallel PDF downloads.
const MAX_CONCURRENT = 3;
let active = 0;
const waiters = [];
const acquire = () =>
  new Promise((resolve) => {
    if (active < MAX_CONCURRENT) {
      active++;
      resolve();
    } else {
      waiters.push(resolve);
    }
  });
const release = () => {
  const next = waiters.shift();
  if (next) {
    next();
  } else {
    active--;
  }
};

const renderCover = async (url) => {
  // disableAutoFetch + disableStream: pdf.js fetches only the byte ranges
  // needed for page 1 instead of downloading the whole PDF. (Without
  // disableStream it still streams the entire file in the background.)
  const doc = await pdfjsLib.getDocument({
    url,
    disableAutoFetch: true,
    disableStream: true,
  }).promise;
  try {
    const page = await doc.getPage(1);
    const { dataUrl } = await renderPageToDataUrl(page, {
      targetWidth: THUMB_WIDTH,
      quality: 0.8,
    });
    return dataUrl;
  } finally {
    doc.destroy();
  }
};

const PdfCoverThumb = ({ url, cacheKey, alt, className, fallback }) => {
  const [dataUrl, setDataUrl] = useState(() => {
    const key = `${CACHE_VERSION}-${cacheKey || url}`;
    if (memCache.has(key)) return memCache.get(key);
    try {
      return sessionStorage.getItem(`pdf-cover-${key}`) || null;
    } catch {
      return null;
    }
  });
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (dataUrl || failed) return;
    let cancelled = false;
    const key = `${CACHE_VERSION}-${cacheKey || url}`;

    const load = async () => {
      await acquire();
      try {
        if (cancelled) return;
        const thumb = await renderCover(url);
        memCache.set(key, thumb);
        try {
          sessionStorage.setItem(`pdf-cover-${key}`, thumb);
        } catch {
          // sessionStorage full — in-memory cache still works
        }
        if (!cancelled) setDataUrl(thumb);
      } catch (err) {
        console.error("PDF cover render error:", err);
        if (!cancelled) setFailed(true);
      } finally {
        release();
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [url, cacheKey, dataUrl, failed]);

  if (dataUrl) {
    return <img src={dataUrl} alt={alt} className={className} />;
  }
  if (failed) {
    return fallback || null;
  }
  // aspect ratio keeps the card's shape while the cover renders,
  // even when className has no fixed height
  return (
    <div className={`${className} aspect-[482/680] bg-gray-200 animate-pulse`} />
  );
};

export default PdfCoverThumb;
